import React, { useState, useEffect } from 'react';
import FormField from '../../components/common/FormField';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import { MemberTypeDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import { useMemberFeeApi } from '../../hooks/useMemberFeeApi';

interface MemberTypeFormProps {
  initialValues: MemberTypeDTO;
  onSubmit: (data: MemberTypeDTO) => void;
  onCancel: () => void;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  settings: {
    id: 'settings',
    title: 'Settings',
  },
} as const;

const MemberTypeForm: React.FC<MemberTypeFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<MemberTypeDTO>(initialValues);
  const [memberFeeOptions, setMemberFeeOptions] = useState<any[]>([]);
  const { search: searchMemberFees } = useMemberFeeApi();

  useEffect(() => {
    const loadMemberFees = async () => {
      try {
        const response = await searchMemberFees({
          filters: { isActive: true },
          page: 1,
          pageSize: 100,
          sortBy: 'Name',
          sortDescending: false,
        });
        
        const options = response?.data?.results?.map((fee: any) => ({
          value: fee.id,
          label: fee.name,
        })) || [];
        
        setMemberFeeOptions(options);
      } catch (error) {
        console.error('Error loading member fees:', error);
      }
    };

    loadMemberFees();
  }, []);

  const validationRules: ValidationRules = {
    title: [{ required: true, message: 'Title is required' }],
    titleShort: [{ required: true, message: 'Title short is required' }],
    displayPosition: [
      { required: true, message: 'Display Position is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Display Position must be a positive number',
      },
    ],
  };

  const { errors, validate } =
    useFormValidation<MemberTypeDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel}>
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required
          error={errors.title}
        />
        <FormField
          id="titleShort"
          label="Title Short"
          type="text"
          value={formData.titleShort || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title Short"
          required
          error={errors.titleShort}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.settings.id}
        title={FORM_SECTIONS.settings.title}
      >
        <FormField
          id="memberFeeId"
          label="Member Fee"
          type="dropdown"
          value={formData.memberFeeId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Member Fee"
          options={memberFeeOptions}
          required={false}
        />
        <FormField
          id="displayPosition"
          label="Position"
          type="number"
          value={formData.displayPosition ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Display Position"
          required={true}
          error={errors.displayPosition}
          helper="Lower numbers will be displayed first."
        />
        <FormField
          id="isActive"
          label="Active?"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Is Active"
          helper="Check to make record active."
        />
      </FormSection>
    </Form>
  );
};

export default MemberTypeForm;
